import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { Suspense, lazy } from "react";
import Layout from "./components/Layout";

const CityRedirect = () => {
  const { citySlug } = useParams();
  return <Navigate to={`/rijschool-${citySlug}`} replace />;
};

const citySlugs = [
  "alphen-aan-den-rijn",
  "ter-aar",
  "boskoop",
  "nieuwkoop",
  "aarlanderveen",
  "zevenhoven",
  "hazerswoude-rijndijk",
  "hazerswoude-dorp",
  "woubrugge",
  "langeraar",
  "koudekerk-aan-den-rijn",
  "zwammerdam",
];
import Index from "./pages/Index";

const Proefles = lazy(() => import("./pages/Proefles"));
const Rijlespakketten = lazy(() => import("./pages/Rijlespakketten"));
const Theorie = lazy(() => import("./pages/Theorie"));
const OverOns = lazy(() => import("./pages/OverOns"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Locatie = lazy(() => import("./pages/Locatie"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Voorwaarden = lazy(() => import("./pages/Voorwaarden"));
const Dienst = lazy(() => import("./pages/Dienst"));
const Vestiging = lazy(() => import("./pages/Vestiging"));
const Printen = lazy(() => import("./pages/Printen"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Index />} />
              <Route
                path="proefles"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Proefles />
                  </Suspense>
                }
              />
              <Route
                path="rijlespakketten"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Rijlespakketten />
                  </Suspense>
                }
              />
              <Route
                path="theorie"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Theorie />
                  </Suspense>
                }
              />
              <Route
                path="over-ons"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <OverOns />
                  </Suspense>
                }
              />
              <Route
                path="blog"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Blog />
                  </Suspense>
                }
              />
              <Route
                path="blog/:slug"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <BlogPost />
                  </Suspense>
                }
              />
              <Route
                path="contact"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Contact />
                  </Suspense>
                }
              />
              <Route
                path="faq"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <FAQ />
                  </Suspense>
                }
              />
              {citySlugs.map((slug) => (
                <Route
                  key={`rijschool-${slug}`}
                  path={`rijschool-${slug}`}
                  element={
                    <Suspense
                      fallback={<div className="p-8 text-center">Laden...</div>}
                    >
                      <Locatie />
                    </Suspense>
                  }
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`rijles-${slug}`}
                  path={`rijles-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`beste-rijschool-${slug}`}
                  path={`beste-rijschool-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`rijschool-omgeving-${slug}`}
                  path={`rijschool-omgeving-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`autorijschool-${slug}`}
                  path={`autorijschool-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`goedkope-rijlessen-${slug}`}
                  path={`goedkope-rijlessen-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              {citySlugs.map((slug) => (
                <Route
                  key={`spoedcursus-rijbewijs-${slug}`}
                  path={`spoedcursus-rijbewijs-${slug}`}
                  element={<Navigate to={`/rijschool-${slug}`} replace />}
                />
              ))}
              <Route path="rijschool/:citySlug" element={<CityRedirect />} />
              <Route
                path="privacy"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Privacy />
                  </Suspense>
                }
              />
              <Route
                path="voorwaarden"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Voorwaarden />
                  </Suspense>
                }
              />
              <Route
                path="rijangst-begeleiding-alphen-aan-den-rijn"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="opfriscursus-autorijden"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="spoedcursus-rijbewijs-alphen-aan-den-rijn"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="2todrive-rijles-vanaf-16-5-jaar"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="overstappen-van-rijschool"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="informatie-voor-ouders"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="rijles-met-adhd-autisme-alphen-aan-den-rijn"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Dienst />
                  </Suspense>
                }
              />
              <Route
                path="tarieven"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Rijlespakketten />
                  </Suspense>
                }
              />
              <Route
                path="vestiging"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Vestiging />
                  </Suspense>
                }
              />
              <Route
                path="printen"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <Printen />
                  </Suspense>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route
                path="*"
                element={
                  <Suspense
                    fallback={<div className="p-8 text-center">Laden...</div>}
                  >
                    <NotFound />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
